import { useLocalization } from 'localization'
import { message } from 'antd'
import { useMutation, useQueryClient } from 'react-query'
import {
	changeOnboardingStatus,
	changeTutorialStatus,
	connectWealthicaUser,
	setReferral,
	syncUserPlan,
	updateUser,
} from './services'
import queries from 'api/queries'
import { ResponseErrorType } from 'types/custom'
import WealthicaAPI from 'api/wealthica/services'
import { useAppContext } from 'modules/common/appContext'
import { useWealthicaSyncIntegrations } from 'api/integrations/mutations'

export const useUpdateUser = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()

	return useMutation(updateUser, {
		onSuccess: (_, { id }) => {
			message.success(t('settings.update.success'))
			queryClient.invalidateQueries([queries.current])
			queryClient.invalidateQueries([queries.userById, id])
			queryClient.invalidateQueries([queries.reports])
			queryClient.invalidateQueries([queries.reportBySlug])
			queryClient.invalidateQueries([queries.analyzeResults])
			queryClient.invalidateQueries([queries.featureAccess])
			queryClient.invalidateQueries([queries.jurisdictions])
			queryClient.invalidateQueries([queries.jurisdictionProperties])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('settings.update.error'))
			}
		},
	})
}

export const useSetReferral = () => {
	const queryClient = useQueryClient()

	return useMutation(setReferral, {
		onSuccess: () => {
			queryClient.invalidateQueries([queries.current])
		},
	})
}

export const useSetOnboardingStatus = () => {
	const { t } = useLocalization()
	const queryClient = useQueryClient()
	const { wealthicaContext, isWealthicaContext } = useAppContext()
	const { mutateAsync: syncWealthicaIntegrations } = useWealthicaSyncIntegrations()

	return useMutation(changeOnboardingStatus, {
		onSuccess: async () => {
			// kick off initial sync for wealthica user
			if (wealthicaContext?.authUser?.email && isWealthicaContext) {
				const { transactions, vezgoTransactions } = await WealthicaAPI.getAllCryptoTransactions()

				if (transactions.length > 0) {
					await syncWealthicaIntegrations({
						mode: 'SYNC_METHOD_OVERRIDE',
						transactions,
						vezgoTransactions,
						silent: true,
					})
				}
			}
			queryClient.invalidateQueries([queries.current])
		},
		onError: (error: ResponseErrorType) => {
			if (error?.response?.status !== 401) {
				message.error(t('settings.onboarding.error'))
			}
		},
	})
}

export const useSetTutorialStatus = () => {
	const queryClient = useQueryClient()

	return useMutation(changeTutorialStatus, {
		onSuccess: () => {
			queryClient.invalidateQueries([queries.tutorials])
		},
	})
}

export const useSyncUserPlan = () => {
	const queryClient = useQueryClient()

	return useMutation(syncUserPlan, {
		retry: false,
		onSuccess: () => {
			queryClient.invalidateQueries([queries.current])
		},
	})
}

export const useConnectWealthicaUser = () => {
	const queryClient = useQueryClient()

	return useMutation(connectWealthicaUser, {
		retry: false,
		onSuccess: () => {
			queryClient.invalidateQueries([queries.current])
		},
	})
}
